// Cross-context flags: an issue that sits near the top of one context's manual
// order (a person's list, the backlog, a sprint) is surfaced everywhere else as
// "this matters to someone's plan". We read every orders/*.json file once and
// build an id → placements index. See n3oltd/work#771.
import { graphql } from './api'
import { ALL_ORDERS } from './github-queries'
import { appConfig } from './query'
import { parseContextOrder } from './context-order'
import type { ContextOrder } from './context-order'

const BRANCH = 'app-data'

/** Only the top few of any context count as a flag — otherwise everything in a
 *  long ordered band would light up and the signal would be worthless. */
export const TOP_N = 3

export type ContextKind = 'personal' | 'backlog' | 'sprint' | 'other'

export type CrossContextPlacement = {
  /** Raw context key, e.g. `mine/sarah`, `backlog`, `sprint/q3-2026/Stage`. */
  context: string
  kind: ContextKind
  /** Owner login for a personal list; null otherwise. */
  owner: string | null
  /** Context label without the person, e.g. "Backlog", "Q3 2026 sprint". */
  label: string
  /** Band/column within the context (a priority band or stage). */
  group: string
  /** 1-based position within the band. */
  rank: number
  /** Login credited with the placement (the owner for personal lists), or null
   *  when the file predates attribution. */
  by: string | null
}

export type OrderIndex = Map<string, CrossContextPlacement[]>

function humanize(slug: string): string {
  return slug.replace(/[-/_]+/g, ' ').trim()
}

/** Classify a context key into how its flag should read. */
export function describeContext(context: string): Pick<CrossContextPlacement, 'kind' | 'owner' | 'label'> {
  const mine = /^mine\/(.+)$/.exec(context)
  if (mine) return { kind: 'personal', owner: mine[1]!, label: 'list' }
  if (context === 'backlog') return { kind: 'backlog', owner: null, label: 'Backlog' }
  const sprint = /^sprint\/(.+?)\/(Stage|Priority)$/.exec(context)
  if (sprint) return { kind: 'sprint', owner: null, label: `${humanize(sprint[1]!)} sprint` }
  return { kind: 'other', owner: null, label: humanize(context) }
}

export type OrderFile = { context: string; order: ContextOrder }

/** Build the id → top-N-placements index from every order file. Pure. */
export function buildOrderIndex(files: OrderFile[]): OrderIndex {
  const index: OrderIndex = new Map()
  for (const { context, order } of files) {
    const meta = describeContext(context)
    for (const [group, ids] of Object.entries(order.groups ?? {})) {
      ids.slice(0, TOP_N).forEach((id, i) => {
        const by = meta.kind === 'personal' ? meta.owner : (order.placements?.[id]?.by ?? null)
        const placement: CrossContextPlacement = { ...meta, context, group, rank: i + 1, by }
        const list = index.get(id)
        if (list) list.push(placement)
        else index.set(id, [placement])
      })
    }
  }
  return index
}

type Entry = { path: string; type: string; object?: { text?: string; entries?: Entry[] } | null }

function collectBlobs(entries: Entry[] | undefined, out: Map<string, string>): void {
  for (const e of entries ?? []) {
    if (e.type === 'blob' && e.path.endsWith('.json') && typeof e.object?.text === 'string') {
      out.set(e.path, e.object.text)
    } else if (e.object?.entries) {
      collectBlobs(e.object.entries, out)
    }
  }
}

/** Read every order file and fold it into the cross-context index. */
export async function loadOrderIndex(): Promise<OrderIndex> {
  const data = await graphql<{ repository: { object: { entries?: Entry[] } | null } | null }>({
    query: ALL_ORDERS,
    variables: { owner: appConfig.owner, name: appConfig.repo, expr: `${BRANCH}:orders` },
  })
  const blobs = new Map<string, string>()
  collectBlobs(data.repository?.object?.entries, blobs)

  const files: OrderFile[] = []
  for (const [path, text] of blobs) {
    const order = parseContextOrder(text) // malformed → empty, contributes nothing
    if (Object.keys(order.groups).length) {
      files.push({ context: path.replace(/^orders\//, '').replace(/\.json$/, ''), order })
    }
  }
  return buildOrderIndex(files)
}
